import { BusinessUser, BusinessUserRole } from '@models/businesses';
import { Encumbrance, EncumbranceType } from '@models/encumbrance';
import {
	ActingForType,
	BusinessMatterClientWithAssociations,
	MatterBusinessUser,
	MatterFileType,
	MatterPropertyDetails,
	MatterTitle,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { LawFirm } from '@models/lawFirms';
import {
	MatterMortgageWithAssociations,
	TransactionType,
} from '@models/mortgage';
import {
	PreviousPayload,
	TitleInsuranceProvider,
	TitlePlusStatus,
} from '@models/title-insurance';
import { UserProfile } from '@models/userProfile';
import { Selector } from '@ngxs/store';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { UserProfileState } from '@state/user-profile';
import { isEqual, cloneDeep } from 'lodash';
import { MatterClients } from '../matter_clients';
import { removeUndefinedAndNull } from './stewart.util';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import {
	CreateDealRequest,
	MatterTitlePlusTitleInsurance,
	PolicyOrder,
	TitlePlusConveyancingUser,
	TitlePlusMatter,
} from '@models/title-insurance/title-plus';
import { buildMatter } from './title-plus.util';
import { BusinessState } from '@state/business';
import { LtvSelectors } from '@state/matter/ltv/ltv.selectors';
import { deepCompare } from '@core/util/value';
import LogRocket from 'logrocket';

export class TitlePlusSelectors {
	private static lastLoggingTimeout: ReturnType<typeof setTimeout>;

	@Selector([
		SelectedMatter.id,
		SelectedMatter.matterNumber,
		SelectedMatter.matterFileType,
		SelectedMatter.closingDate,
		SelectedMatter.getTitleInsuranceResolvedPurchasePrice,
		SelectedMatter.otherPartyLawFirm,
		SelectedMatter.propertyDetails,
		MatterClients.get_mortgagorsWithAssociationMapped,
		MatterClients.get_otherPartyWithAssociationMapped,
		MatterMortgagesState.getAllMortgagesWithAssociations,
		MatterEncumbrancesState.getRemain,
		SelectedMatter.titleInsurancePreviousSubmittedPayload,
		SelectedMatter.actingFor,
		BusinessUsersState.getUsers,
		SelectedMatter.assignedLawyer,
		SelectedMatter.assignedClerk,
		UserProfileState.getProfile,
		SelectedMatter.getTitleInsuranceProvider,
		MatterTitlesState.getAll,
		BusinessState.businessName,
		LtvSelectors.propertyValue,
	])
	static getTitlePlusDataPayload(
		matterId: string,
		matterNumber: string,
		fileType: MatterFileType,
		closingDate: string,
		purchasePrice: number,
		otherPartyFirm: LawFirm,
		propertyDetails: MatterPropertyDetails,
		purchasers: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		vendors: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		allMortgages: MatterMortgageWithAssociations[],
		allRemains: Encumbrance[],
		previousPayload: PreviousPayload,
		actingFor: ActingForType,
		allUsers: BusinessUser[],
		assignedLawyer: MatterBusinessUser,
		assignedCLerk: MatterBusinessUser,
		user: UserProfile,
		provider: TitleInsuranceProvider,
		matterTitles: MatterTitle[],
		businessName: string,
		propertyValue: number,
	): CreateDealRequest {
		if (!matterId || provider !== TitleInsuranceProvider.TITLE_PLUS) {
			return null;
		}

		const mortgages = allMortgages?.filter(
			(x) => x.transactionType !== TransactionType.BRIDGE && !x.mms,
		);
		const remains = allRemains?.filter(
			(x) => x.type === EncumbranceType.MORTGAGE,
		);

		const Matter: TitlePlusMatter = buildMatter(
			matterNumber,
			closingDate,
			propertyDetails,
			assignedLawyer,
			assignedCLerk,
			allUsers,
			businessName,
			otherPartyFirm,
			fileType,
			mortgages,
			purchasePrice,
			matterTitles,
			purchasers,
			vendors,
			remains,
			propertyValue,
			user,
			actingFor,
		);

		const PolicyOrder: PolicyOrder = {
			SecureId: previousPayload
				? (previousPayload as CreateDealRequest).PolicyOrder?.SecureId
				: 'new',
			Matter,
		};

		const ConveyancingUser: TitlePlusConveyancingUser = {
			FirstName: user.firstName,
			LastName: user.lastName,
			Email: user.email,
			ProfileId: user.id,
			Role: user.role === BusinessUserRole.LAWYER ? 'Lawyer' : 'Clerk',
		};

		const dealRequest: CreateDealRequest = {
			Partner: 'LawLabs',
			PolicyOrder,
			ConveyancingUser,
		};

		return dealRequest;
	}

	@Selector([SelectedMatter.getTitleInsurance])
	static previousPayload(
		titleInsurance: MatterTitlePlusTitleInsurance,
	): CreateDealRequest {
		return titleInsurance?.previousSubmittedPayload;
	}

	@Selector([
		TitlePlusSelectors.getTitlePlusDataPayload,
		SelectedMatter.getTitleInsurance,
	])
	static getPayloadHasChanged(
		curr: CreateDealRequest,
		titleInsurance: MatterTitlePlusTitleInsurance,
	): boolean {
		let payloadHasChanged = true;

		if (titleInsurance?.provider !== TitleInsuranceProvider.TITLE_PLUS) {
			return false;
		}

		if (!titleInsurance?.previousSubmittedPayload) {
			return false;
		}

		if (titleInsurance?.previousSubmittedPayload && !curr) {
			return true;
		}

		const previousPayload = cloneDeep(titleInsurance?.previousSubmittedPayload);
		const currentPayload = cloneDeep(curr);

		let changes = [];

		if (
			isEqual(
				removeUndefinedAndNull(previousPayload),
				removeUndefinedAndNull(currentPayload),
			)
		) {
			payloadHasChanged = false;
		} else {
			changes = deepCompare(
				removeUndefinedAndNull(previousPayload),
				removeUndefinedAndNull(currentPayload),
			);
		}

		TitlePlusSelectors.lastLoggingTimeout = setTimeout(() => {
			LogRocket.log('Current Title Plus payload', currentPayload);
			LogRocket.log('Previous Title Plus payload', previousPayload);
			LogRocket.log('Title Plus payload has changed', payloadHasChanged);

			if (payloadHasChanged) {
				changes.forEach((change) => {
					LogRocket.log(
						`Title Plus property ${change.path} has changed. Before: ${change.before}, After: ${change.after}`,
					);
				});
			}
		}, 2000);

		return payloadHasChanged;
	}

	@Selector([
		TitlePlusSelectors.getPayloadHasChanged,
		SelectedMatter.getTitleInsurance,
	])
	static payloadWarning(
		payloadChanged: boolean,
		titleInsurance: MatterTitlePlusTitleInsurance,
	): boolean {
		if (
			[TitlePlusStatus.CANCELLED, TitlePlusStatus.DECLINED].includes(
				titleInsurance?.state as TitlePlusStatus,
			)
		) {
			return false;
		}

		return payloadChanged;
	}
}
